/**
 * CategoryGroup — collapsible section grouping tools by category.
 *
 * Uses Radix UI Collapsible for expand/collapse with smooth animation.
 * Expanded state is tracked via useToolBrowserStore.expandedGroups.
 *
 * Phase 5 — Unit 5.5
 */

import { ChevronRight } from 'lucide-react';
import React from 'react';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { useToolBrowserStore } from '@/store/useToolBrowserStore';

export interface CategoryGroupProps {
  /** Display name of the category */
  categoryName: string;
  /** Category icon (Lucide icon or text) */
  icon?: React.ReactNode;
  /** Number of tools in this category */
  toolCount: number;
  /** Content rendered inside the collapsible body */
  children: React.ReactNode;
  /** When true, force the group open regardless of store state (e.g., during search) */
  forceOpen?: boolean;
}

export const CategoryGroup: React.FC<CategoryGroupProps> = ({
  categoryName,
  icon,
  toolCount,
  children,
  forceOpen,
}) => {
  const expandedGroups = useToolBrowserStore((s) => s.expandedGroups);
  const toggleGroup = useToolBrowserStore((s) => s.toggleGroup);

  const isExpanded = forceOpen || expandedGroups.has(categoryName);

  return (
    <Collapsible open={isExpanded} onOpenChange={() => { if (!forceOpen) toggleGroup(categoryName); }}>
      <CollapsibleTrigger asChild>
        <button
          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium
                     hover:bg-accent/60 transition-colors"
          aria-label={`${categoryName} — ${toolCount} tools`}
        >
          <ChevronRight
            className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200', isExpanded && 'rotate-90')}
          />
          {icon && <span className="text-sm inline-flex items-center">{icon}</span>}
          <span className="flex-1 text-left truncate">{categoryName}</span>
          <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs text-muted-foreground tabular-nums">
            {toolCount}
          </span>
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent className="overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up">
        <div className="ml-4 border-l border-border pl-2 py-1 space-y-0.5">
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};


